// src/tabs/messages/segment-editor/VarPickerButton.jsx
//
// Bouton « + Info voyageur » + menu de variables, EXTRAIT de SegmentEditor pour
// être réutilisable :
//   • Plateformes / Direct historique : monté PAR CHAMP dans SegmentEditor
//     (prop showVarPicker par défaut true) → comportement inchangé.
//   • Direct (M12+B4) : monté UNE SEULE FOIS dans DirectMessagesView, au-dessus
//     des deux champs ; insère la variable dans le CHAMP ACTIF (objet/corps) via
//     la ref exposée par chaque SegmentEditor (insertVariable).
//
// Pur composant d'UI : il ne connaît ni le caret ni l'arbre — il rend des
// libellés et remonte la clé choisie via onPick(key). L'insertion (au caret
// mémorisé du champ) reste la responsabilité du SegmentEditor cible.

import { useMemo, useState } from "react";
import { User } from "lucide-react";
import { DS } from "../../../lib/ui";
import { humanPickerLabel } from "./variable-labels";

const ghostBtn = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  fontSize: 12,
  color: DS.marine,
  border: `0.5px solid ${DS.borderHover}`,
  borderRadius: 8,
  padding: "4px 11px",
  background: DS.card,
  cursor: "pointer",
  fontFamily: DS.fontBody,
};
const ghostBtnDisabled = {
  ...ghostBtn,
  opacity: 0.45,
  cursor: "not-allowed",
};
const popover = {
  position: "absolute",
  top: 32,
  left: 0,
  background: DS.card,
  border: `0.5px solid ${DS.border}`,
  borderRadius: 10,
  boxShadow: "0 8px 24px rgba(0,0,0,.15)",
  width: "min(320px, calc(100vw - 40px))",
  maxHeight: 360,
  overflowY: "auto",
  zIndex: 20,
  padding: 8,
};
const popoverItem = {
  display: "block",
  width: "100%",
  textAlign: "left",
  padding: "5px 8px",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  fontSize: 11.5,
  color: DS.marineDark,
  borderRadius: 5,
  fontFamily: DS.fontBody,
};

// varGroups : [{ category, variables:[{key,label,insertAs?}] }]. labelFor
// (Direct) prime sur humanPickerLabel. Les entrées insertAs:'block' (insérées
// via « Paragraphe conditionnel ») sont exclues du picker de variables.
export default function VarPickerButton({
  varGroups,
  labelFor = null,
  onPick,
  disabled = false,
  title,
  // M1 : ouverture CONTRÔLABLE (optionnel). Fournie par SegmentEditor pour
  // l'exclusion mutuelle avec le menu « + Paragraphe conditionnel ». Absente
  // (bouton partagé Direct) → état interne non contrôlé.
  open: openProp,
  onOpenChange,
}) {
  const [openState, setOpenState] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : openState;
  const setOpen = (v) =>
    controlled ? onOpenChange && onOpenChange(v) : setOpenState(v);

  const pickerGroups = useMemo(() => {
    const groups = varGroups || [];
    return groups
      .map((g) => ({
        ...g,
        variables: (g.variables || []).filter((v) => v.insertAs !== "block"),
      }))
      .filter((g) => g.variables.length > 0);
  }, [varGroups]);

  return (
    <div style={{ position: "relative" }}>
      <button
        type="button"
        className="fbz-press"
        data-testid="varpicker-btn"
        disabled={disabled}
        title={title}
        onClick={() => !disabled && setOpen(!open)}
        style={disabled ? ghostBtnDisabled : ghostBtn}
      >
        <User size={13} strokeWidth={2} /> + Info voyageur
      </button>
      {open && !disabled && (
        <div style={popover}>
          {pickerGroups.length === 0 && (
            <div style={{ padding: "6px 8px", fontSize: 11, color: DS.textMuted }}>
              Aucune variable disponible.
            </div>
          )}
          {pickerGroups.map((group) => (
            <div key={group.category} style={{ marginBottom: 6 }}>
              <div
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: DS.marine,
                  padding: "3px 8px",
                }}
              >
                {group.category}
              </div>
              {group.variables.map((v) => {
                // labelFor (M12 Direct) prime ; sinon libellé court validé — le
                // libellé technique long reste en tooltip.
                const shown = (labelFor && labelFor(v.key)) || humanPickerLabel(v);
                return (
                  <button
                    key={v.key}
                    type="button"
                    data-testid="varpicker-item"
                    onClick={() => {
                      setOpen(false);
                      onPick(v.key);
                    }}
                    style={popoverItem}
                    title={v.label && v.label !== shown ? v.label : undefined}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = DS.pageBg)
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                  >
                    {shown}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
